import {REDACTED_LOG_VALUE} from '../constants/request';

import {isSensitiveHeaderName} from './redaction';

const normalizeHeaderValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const values = value.flatMap((item) => {
      const asString = String(item);

      return asString ? [asString] : [];
    });

    return values.length > 0 ? values.join(', ') : undefined;
  }

  return String(value);
};

/** Converts any `HeadersInit` variant (Headers, [key,value][], Record) to a plain `[string, unknown][]`. */
const toEntries = (headers: HeadersInit): [string, unknown][] => {
  if (Array.isArray(headers)) return headers;

  if ('entries' in headers && typeof headers.entries === 'function') {
    return Array.from(headers.entries()) as [string, unknown][];
  }

  return Object.entries(headers);
};

/** Converts a `Headers` instance to a plain `Record<string, string>`. */
export const toHeadersRecord = (headers: Headers): Record<string, string> => {
  return Object.fromEntries(Array.from(headers.entries()));
};

/** Converts any `HeadersInit` variant (Headers, [key,value][], Record) to a plain `Record<string, string>`. */
export const normalizeHeadersInit = (
  headers?: HeadersInit,
): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }

  const normalized: Record<string, string> = {};

  for (const [key, value] of toEntries(headers)) {
    const normalizedValue = normalizeHeaderValue(value);

    if (normalizedValue === undefined) {
      continue;
    }

    normalized[key] = normalizedValue;
  }

  return normalized;
};

/** Redacts authorization, cookie, and API key headers so they can be safely logged or sent to OTel. */
export const sanitizeHeadersForLog = (
  headers?: HeadersInit | Record<string, string>,
): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }

  const sanitized: Record<string, string> = {};

  for (const [key, value] of toEntries(headers)) {
    const normalizedValue = normalizeHeaderValue(value);

    if (normalizedValue === undefined) {
      continue;
    }

    sanitized[key] = isSensitiveHeaderName(key)
      ? REDACTED_LOG_VALUE
      : normalizedValue;
  }

  return sanitized;
};
